/**
 * Client-lib voor refunds — typed fetch-helpers + error class.
 *
 * Wraps `/api/orders/refund` (POST) en `/api/orders/refunds-list` (GET).
 * Types matchen met `api/orders/refund.ts` + `KledaRefundRest.cls`.
 */

import { supabase } from './supabase';

// ─── Types ───────────────────────────────────────────────────────────────────

export type RefundMethode = 'Mollie' | 'Bank' | 'Coulance_Tegoed' | 'Factuur_Correctie';
export type RefundStatus = 'Pending' | 'Verwerkt' | 'Mislukt';
export type RefundReden =
  | 'Klacht'
  | 'Annulering'
  | 'Overbetaling'
  | 'Coulance'
  | 'Webshop-Retour'
  | 'Overig';

export interface RefundCreateInput {
  werkOrderId: string;
  amountIncBtw: number;
  methode: RefundMethode;
  refundReden: RefundReden;
  redenVoorMollie?: string;
  toelichting?: string;
  klachtId?: string;
  molliePaymentIdOverride?: string;
  externalId?: string;
}

export interface RefundCreateResult {
  ok: boolean;
  refundId?: string;
  refundName?: string;
  mollieRefundId?: string;
  status?: RefundStatus | string;
  amountRefundedAfter?: number;
  idempotent?: boolean;
}

export interface RefundListItem {
  refundId: string;
  refundName: string;
  bedragInclBtw: number;
  methode: RefundMethode | string;
  status: RefundStatus | string;
  refundReden?: string;
  reden?: string;
  toelichting?: string;
  bron?: string;
  mollieRefundId?: string;
  mollie_payment_id?: string;
  datumVerwerkt?: string;
  createdDate?: string;
  foutmelding?: string;
  klachtId?: string;
}

// ─── Errors ──────────────────────────────────────────────────────────────────

export class RefundError extends Error {
  status: number;
  errorCode?: string;
  constructor(message: string, status: number, errorCode?: string) {
    super(message);
    this.name = 'RefundError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

async function parseError(res: Response, fallback: string): Promise<RefundError> {
  const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  return new RefundError(body?.error || fallback, res.status, body?.code);
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export async function createRefund(input: RefundCreateInput): Promise<RefundCreateResult> {
  const res = await fetch('/api/orders/refund', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseError(res, 'Refund mislukt');
  return res.json() as Promise<RefundCreateResult>;
}

// ─── Queries ─────────────────────────────────────────────────────────────────

export async function listRefunds(werkOrderId: string): Promise<RefundListItem[]> {
  const url = `/api/orders/refunds-list?werkOrderId=${encodeURIComponent(werkOrderId)}`;
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw await parseError(res, 'Refund-lijst ophalen mislukt');
  const body = (await res.json()) as { refunds?: RefundListItem[] };
  return body.refunds || [];
}
